/**
 * @fileoverview Acciones para la gestión y carga de agentes del inbox por usuario y por rol.
 * Proporciona funciones para obtener, establecer y manejar errores de agentes usando el modelo RetailerUserModelMC.
 * @module actions/inbox/retailerUserActions
 * @category Actions Layer
 * @since 2.0.0
 * @version 3.1.0
 */

/**
 * @typedef {Object} RetailerUser
 * @description Estructura de datos para un agente del inbox.
 * @property {string|number} id - Identificador único del agente.
 * @property {string} name - Nombre del agente.
 * @property {string} role - Rol del agente (admin, supervisor, agente, etc.).
 * @property {boolean} [active] - Estado de actividad del agente.
 */

import RetailerUserModelMC from '../../presentation/models/inbox/RetailerUserModelMC';

/**
 * Acción asíncrona para obtener todos los agentes del inbox siempre que no estén en estado de carga.
 *
 * @function fetchInboxAgentsAlways
 * @category Actions Layer
 * @returns {Promise<void>} No retorna valor, solo despacha acciones.
 * @example
 * await fetchInboxAgentsAlways()(dispatch, getState);
 * @see {@link RetailerUserModelMC.getRetailerUsers}
 */
export const fetchInboxAgentsAlways = () => async (dispatch, getState) => {
  const { loading } = getState().inboxAgentsReducer;

  // Si está cargando, no volver a llamar
  if (loading) return;

  dispatch({ type: 'INBOX_AGENTS_LOADING' });
  try {
    const data = await RetailerUserModelMC.getRetailerUsers();
    dispatch({ type: 'SET_INBOX_AGENTS_DATA', data });
  } catch (err) {
    dispatch({ type: 'INBOX_AGENTS_FAILURE', err });
  }
};

/**
 * Acción asíncrona para obtener agentes del inbox solo si el array está vacío y no se está cargando.
 *
 * @function fetchInboxAgentsIfEmpty
 * @category Actions Layer
 * @returns {Promise<void>} No retorna valor, solo despacha acciones.
 * @example
 * await fetchInboxAgentsIfEmpty()(dispatch, getState);
 * @see {@link RetailerUserModelMC.getRetailerUsers}
 */
export const fetchInboxAgentsIfEmpty = () => async (dispatch, getState) => {
  const { inboxAgentList, loading } = getState().inboxAgentsReducer;

  // Si está cargando, no volver a llamar
  if (loading) return;

  if (!inboxAgentList.length) {
    dispatch({ type: 'INBOX_AGENTS_LOADING' });
    try {
      const data = await RetailerUserModelMC.getRetailerUsers();
      dispatch({ type: 'SET_INBOX_AGENTS_DATA', data });
    } catch (err) {
      dispatch({ type: 'INBOX_AGENTS_FAILURE', err });
    }
  } else {
    /**
     * Acción informativa: los agentes ya están cargados, no se realiza nueva petición.
     * @category Redux Action
     */
    dispatch({ type: 'INBOX_AGENTS_ALREADY_LOADED' });
  }
};

/**
 * Acción asíncrona para obtener agentes del inbox por rol siempre que no estén en estado de carga.
 *
 * @function fetchInboxAgentsByRoleAlways
 * @category Actions Layer
 * @param {Object} params - Parámetros para filtrar agentes por rol.
 * @returns {Promise<void>} No retorna valor, solo despacha acciones.
 * @example
 * await fetchInboxAgentsByRoleAlways({ role: 'admin' })(dispatch, getState);
 * @see {@link RetailerUserModelMC.getRetailerUsersByRole}
 */
export const fetchInboxAgentsByRoleAlways = (params) => async (dispatch, getState) => {
  const { loadingByRole } = getState().inboxAgentsReducer;

  // Si está cargando, no volver a llamar
  if (loadingByRole) return;

  dispatch({ type: 'INBOX_AGENTS_BY_ROLE_LOADING' });
  try {
    const data = await RetailerUserModelMC.getRetailerUsersByRole(params);
    dispatch({ type: 'SET_INBOX_AGENTS_BY_ROLE_DATA', data });
  } catch (err) {
    dispatch({ type: 'INBOX_AGENTS_BY_ROLE_FAILURE', err });
  }
};

/**
 * Acción asíncrona para obtener agentes del inbox por rol solo si el array está vacío y no se está cargando.
 *
 * @function fetchInboxAgentsByRoleIfEmpty
 * @category Actions Layer
 * @param {Object} params - Parámetros para filtrar agentes por rol.
 * @returns {Promise<void>} No retorna valor, solo despacha acciones.
 * @example
 * await fetchInboxAgentsByRoleIfEmpty({ role: 'agente' })(dispatch, getState);
 * @see {@link RetailerUserModelMC.getRetailerUsersByRole}
 */
export const fetchInboxAgentsByRoleIfEmpty = (params) => async (dispatch, getState) => {
  const { inboxAgentListByRole, loadingByRole } = getState().inboxAgentsReducer;

  // Si está cargando, no volver a llamar
  if (loadingByRole) return;

  if (!inboxAgentListByRole.length) {
    dispatch({ type: 'INBOX_AGENTS_BY_ROLE_LOADING' });
    try {
      const data = await RetailerUserModelMC.getRetailerUsersByRole(params);
      dispatch({ type: 'SET_INBOX_AGENTS_BY_ROLE_DATA', data });
    } catch (err) {
      dispatch({ type: 'INBOX_AGENTS_BY_ROLE_FAILURE', err });
    }
  } else {
    /**
     * Acción informativa: los agentes por rol ya están cargados, no se realiza nueva petición.
     * @category Redux Action
     */
    dispatch({ type: 'INBOX_AGENTS_BY_ROLE_ALREADY_LOADED' });
  }
};
